import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import GeneralInfosForm from "../components/TenantCheckForm/Forms/GeneralInfosForm";
import TenantCheckFormHeader from "../components/TenantCheckForm/TenantCheckFormHeader";
import Wrapper from "../components/layout/Wrapper";
import { useProperty } from "../hooks/properties/useProperty";
import { useCandidate } from "../hooks/tenants/useCandidate";
import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import FinalReviewContainer from "../components/TenantCheckForm/FinalReviewContainer";
import { useUserAuth } from "../context/UserAuthContext";
import styled from "styled-components";
import NAV from "../utils/nav.const";
import theme from "../styles/styleTheme";
import LoadingAnimation from "../components/common/LoadingAnimation";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { formatCurrency } from "../utils/currency";
import ProfileManagement from "../components/TenantCheckForm/ProfileManagement";
import { checkProfileCompleteness } from "../utils/profileUtils";
import SubmittedScreeningView from "../components/TenantCheckForm/SubmittedScreeningView";
import { useTranslation } from "react-i18next";
import { BoxPaper } from "../components/common/BoxPaper";

const TenantCheck = () => {
  const { t } = useTranslation();
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [triggers, setTriggers] = useState<any[]>([]);
  const [openSection, setOpenSection] = useState<string | null>("generalInfo");
  const [allLinkedProfilesComplete, setAllLinkedProfilesComplete] =
    useState(false);
  const [hasRenterProfile, setHasRenterProfile] = useState(false);
  const [generalInfoComplete, setGeneralInfoComplete] = useState(false);
  const {
    getDataById: getTenantById,
    data: screeningData,
    loading: screeningLoading,
  } = useCandidate();
  const {
    getDataById: getPropertyById,
    data: property,
    loading: propertyLoading,
  } = useProperty();
  const { user, loading: userLoading } = useUserAuth();

  useEffect(() => {
    if (screeningData != null) {
      getPropertyById(screeningData.candidate.propertyId);
    }
  }, [screeningData]);

  useEffect(() => {
    if (!userLoading && user?.tenant) {
      getTenantById(candidateId);
    }
  }, [candidateId, user, openSection]);

  const onhandleGoogleReview = () => {
    window.open(
      "https://www.google.com/search?sca_esv=9a5e7dbe56d12357&rlz=1C5CHFA_enRO1093RO1093&tbm=lcl&q=GoHome+Reviews&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxIxNDY3NTM1NzAxsDQ3swSSFhZmGxgZXzHyued75OemKgSllmWmlhcvYkUTAAA7YUlvPQAAAA&rldimm=13756570409769409886&hl=nl-RO&sa=X&ved=2ahUKEwij6vqciJuKAxWG1AIHHVfmGkYQ9fQKegQITRAF&biw=1920&bih=1079&dpr=2#lkt=LocalPoiReviews&lrd=0x47c6271d0871d96f:0xbee927e6f9f49d5e,3",
      "_blnk"
    );
  };

  return (
    <Wrapper>
      {!user && !userLoading && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={12}>
            <BoxPaper>
              <Typography variant="h1">{t("screening.title")}</Typography>
              <Box
                sx={{
                  margin: "10vh 0",
                  marginLeft: 0,
                  textAlign: "center",
                }}
              >
                <Typography>{t("screening.not_logged_in")}</Typography>
                <Button
                  sx={{
                    marginTop: "2rem",
                    padding: ".5rem 2rem",
                    backgroundColor: theme.palette.primary.main,
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                    },
                  }}
                  onClick={() => {
                    navigate(
                      `/${NAV.SIGNIN}?redirect=${encodeURIComponent(
                        location.pathname + location.search
                      )}`
                    );
                  }}
                >
                  {t("screening.go_to_login")}
                </Button>
              </Box>
            </BoxPaper>
          </Grid>
        </Grid>
      )}
      {screeningLoading && !screeningData && user && !userLoading && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {!screeningData?.candidate?.submitted && (
              <TenantCheckFormHeader property={property} />
            )}
          </Grid>
          <Grid item xs={12}>
            <BoxPaper>
              <LoadingAnimation />
            </BoxPaper>
          </Grid>
        </Grid>
      )}

      {!screeningLoading && !screeningData && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={12}>
            <BoxPaper>
              <Typography variant="h1">{t("screening.not_found")}</Typography>
              <Box
                sx={{
                  margin: "10vh 0",
                  marginLeft: 0,
                  textAlign: "center",
                }}
              >
                <Typography>{t("screening.not_found_description")}</Typography>
                <Button
                  sx={{
                    marginTop: "2rem",
                    padding: ".5rem 2rem",
                    backgroundColor: theme.palette.primary.main,
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                    },
                  }}
                  onClick={() => {
                    navigate("/" + NAV.APPLICATIONS);
                  }}
                >
                  {t("screening.go_to_applications")}
                </Button>
              </Box>
            </BoxPaper>
          </Grid>
        </Grid>
      )}

      {screeningData && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={12}>
            {!screeningData?.candidate?.submitted && (
              <TenantCheckFormHeader property={property} />
            )}
          </Grid>

          <Grid item xs={12}>
            <BoxPaper>
              {screeningData && !screeningLoading && (
                <>
                  {!screeningData?.candidate.submitted && (
                    <>
                      <GeneralInfosForm
                        candidate={screeningData?.candidate}
                        property={property}
                        setTriggers={setTriggers}
                        setGeneralInfoComplete={setGeneralInfoComplete}
                      />
                      <ProfileManagement
                        candidateId={candidateId}
                        tenantId={user?.tenant?.id}
                        setAllLinkedProfilesComplete={
                          setAllLinkedProfilesComplete
                        }
                        setHasRenterProfile={setHasRenterProfile}
                      />
                      <FinalReviewContainer
                        allLinkedProfilesComplete={allLinkedProfilesComplete}
                        generalInfoComplete={generalInfoComplete}
                        hasRenterProfile={hasRenterProfile}
                        candidate={screeningData?.candidate}
                        triggers={triggers}
                      />
                    </>
                  )}
                </>
              )}
              {screeningData &&
                property &&
                screeningData?.candidate?.submitted && (
                  <SubmittedScreeningView
                    property={property}
                    screeningData={screeningData}
                    onGoogleReview={onhandleGoogleReview}
                  />
                )}
            </BoxPaper>
          </Grid>
        </Grid>
      )}
    </Wrapper>
  );
};

export default TenantCheck;
